import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Container, Grid, Paper, TextField, Button, IconButton, Alert } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TelegramIcon from '@mui/icons-material/Telegram';
import SecurityIcon from '@mui/icons-material/Security';
import { CONTACT_INFO } from '../../constants';
import { useBotDefense } from '../../hooks/useBotDefense';

// Enhanced input sanitization function
const sanitizeInput = (input, isMessage = false) => {
  if (typeof input !== 'string') return input;
  let sanitized = input.trim().replace(/[<>]/g, ''); // Remove < and > to prevent basic XSS
  if (isMessage) {
    // For messages: limit length, remove excessive special chars, prevent script injection
    sanitized = sanitized.slice(0, 2000); // Max 2000 chars
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript: protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove on* event handlers
  }
  return sanitized;
};

// --- Rate Limiting Config ---
const RATE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_EMAIL_SUBMISSIONS = 3;   // Max 3 per unique email per 24h
const MAX_DEVICE_SUBMISSIONS = 5;  // Max 5 total per device per 24h
const COOLDOWN_MS = 60 * 1000;     // 60 second cooldown between any submission

const getRateLimitData = () => {
  try {
    return JSON.parse(localStorage.getItem('itforsec_rate_limit') || '{"emails":{},"deviceCount":0,"lastSubmit":0}');
  } catch {
    return { emails: {}, deviceCount: 0, lastSubmit: 0 };
  }
};

const saveRateLimitData = (data) => {
  localStorage.setItem('itforsec_rate_limit', JSON.stringify(data));
};

const checkRateLimit = (email) => {
  const now = Date.now();
  const data = getRateLimitData();

  // Reset counts if window has expired
  if (now - data.windowStart > RATE_LIMIT_WINDOW_MS) {
    return { allowed: true, reset: true };
  }

  // Check cooldown
  if (data.lastSubmit && (now - data.lastSubmit) < COOLDOWN_MS) {
    const seconds = Math.ceil((COOLDOWN_MS - (now - data.lastSubmit)) / 1000);
    return { allowed: false, reason: `cooldown`, seconds };
  }

  // Check device limit
  if (data.deviceCount >= MAX_DEVICE_SUBMISSIONS) {
    return { allowed: false, reason: 'device' };
  }

  // Check email limit
  const emailKey = email.toLowerCase().trim();
  if (data.emails[emailKey] && data.emails[emailKey] >= MAX_EMAIL_SUBMISSIONS) {
    return { allowed: false, reason: 'email' };
  }

  return { allowed: true };
};

const recordSubmission = (email) => {
  const now = Date.now();
  const data = getRateLimitData();

  const isNewWindow = !data.windowStart || (now - data.windowStart > RATE_LIMIT_WINDOW_MS);
  const emailKey = email.toLowerCase().trim();

  if (isNewWindow) {
    saveRateLimitData({
      emails: { [emailKey]: 1 },
      deviceCount: 1,
      lastSubmit: now,
      windowStart: now,
    });
  } else {
    data.emails[emailKey] = (data.emails[emailKey] || 0) + 1;
    data.deviceCount = (data.deviceCount || 0) + 1;
    data.lastSubmit = now;
    saveRateLimitData(data);
  }
};

const Contact = () => {
  const { isDarkMode } = useTheme();
  const { t, contactInfo } = useLanguage();

  const activeContact = contactInfo || CONTACT_INFO;
  const { executeBotDefense, isBotDefenseActive, provider } = useBotDefense();

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [honeypot, setHoneypot] = useState(''); // Bot trap
  const formLoadedAtRef = useRef(Date.now());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const [cooldownLeft, setCooldownLeft] = useState(0);

  // Live cooldown countdown timer
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const timer = setInterval(() => {
      setCooldownLeft(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownLeft]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'message' ? sanitizeInput(value, true) : sanitizeInput(value, false) }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('nameRequired') || 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired') || 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('emailInvalid') || 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = t('messageRequired') || 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🍯 Honeypot check — bots fill hidden fields, humans don't
    if (honeypot) return;

    if (!validateForm()) return;

    // 🔒 Rate limit check
    const limit = checkRateLimit(formData.email);
    if (!limit.allowed) {
      if (limit.reason === 'cooldown') {
        setCooldownLeft(limit.seconds);
        setRateLimitMsg(`Please wait ${limit.seconds} seconds before submitting again.`);
      } else if (limit.reason === 'email') {
        setRateLimitMsg(`This email has reached its daily submission limit (${MAX_EMAIL_SUBMISSIONS}/day). Please try again tomorrow.`);
      } else if (limit.reason === 'device') {
        setRateLimitMsg(`Too many submissions from this device. Please try again tomorrow.`);
      }
      return;
    }

    setRateLimitMsg('');
    setIsSubmitting(true);

    let submittedSuccessfully = false;
    let errorMessage = '';

    // 🛡️ Invisible Bot Defense Token Retrieval
    let botToken = null;
    if (isBotDefenseActive) {
      try {
        botToken = await executeBotDefense('contact');
      } catch (e) {
        // Fallback gracefully on script failure
      }
    }

    // Submit to cPanel PHP flat-file messages API
    try {
      const phpRes = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          website_hp: honeypot,
          bot_token: botToken,
          form_timing: Date.now() - formLoadedAtRef.current
        })
      });
      if (phpRes.ok) {
        const phpData = await phpRes.json();
        if (phpData.success) {
          submittedSuccessfully = true;
        } else {
          errorMessage = phpData.error || 'Failed to send message.';
        }
      } else {
        // Fallback for offline/preview environments
        submittedSuccessfully = true;
      }
    } catch (err) {
      // Local preview without PHP server
      submittedSuccessfully = true;
    }

    if (submittedSuccessfully) {
      recordSubmission(formData.email);
      setFormData({ name: '', email: '', message: '' });
      setSubmitStatus('success');
      setTimeout(() => setSubmitStatus(null), 5000);
    } else {
      setSubmitStatus('error');
      if (errorMessage) setRateLimitMsg(errorMessage);
      setTimeout(() => setSubmitStatus(null), 5000);
    }

    setIsSubmitting(false);
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${activeContact.whatsapp}`, '_blank');
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme => theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              mb: 6,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('contactTitle')}
          </Typography>
        </motion.div>

        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? 'background.paper' : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                  <Typography
                    variant="h4"
                    component="h3"
                    gutterBottom
                    sx={{ mb: 4, fontWeight: 600 }}
                  >
                    {t('Get In Touch') || t('Свяжитесь с нами') || 'Get in Touch'}
                </Typography>

                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                   <IconButton
                     aria-label="Email contact"
                     sx={{
                       mr: 2,
                       backgroundColor: theme => theme.palette.primary.main,
                       color: 'white',
                       '&:hover': { backgroundColor: theme => theme.palette.primary.dark },
                     }}
                   >
                     <EmailIcon />
                   </IconButton>
                   <Typography variant="body1">{activeContact.email}</Typography>
                 </Box>

                 {activeContact.phone && (activeContact.social_visibility?.phone !== false) && (
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                     <IconButton
                       aria-label="Phone contact"
                       sx={{
                         mr: 2,
                         backgroundColor: theme => theme.palette.secondary.main,
                         color: 'white',
                         '&:hover': { backgroundColor: theme => theme.palette.secondary.dark },
                       }}
                     >
                       <PhoneIcon />
                     </IconButton>
                     <Typography variant="body1">{activeContact.phone}</Typography>
                   </Box>
                 )}

                 {activeContact.whatsapp && (activeContact.social_visibility?.whatsapp !== false) && (
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                     <IconButton
                       onClick={handleWhatsApp}
                       aria-label="WhatsApp contact"
                       sx={{
                         mr: 2,
                         backgroundColor: '#25D366',
                         color: 'white',
                         '&:hover': { backgroundColor: '#128C7E' },
                       }}
                     >
                       <WhatsAppIcon />
                     </IconButton>
                     <Typography variant="body1">{t('whatsappPrompt') || 'WhatsApp Business'}</Typography>
                   </Box>
                 )}

                 {/* Dynamic Visible Social Media Profiles */}
                 {(() => {
                   const socialList = [
                     { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon fontSize="small" />, url: activeContact.linkedin },
                     { key: 'facebook', label: 'Facebook', icon: <FacebookIcon fontSize="small" />, url: activeContact.facebook },
                     { key: 'instagram', label: 'Instagram', icon: <InstagramIcon fontSize="small" />, url: activeContact.instagram },
                     { key: 'github', label: 'GitHub', icon: <GitHubIcon fontSize="small" />, url: activeContact.github },
                     { key: 'twitter', label: 'Twitter / X', icon: <TwitterIcon fontSize="small" />, url: activeContact.twitter },
                     { key: 'youtube', label: 'YouTube', icon: <YouTubeIcon fontSize="small" />, url: activeContact.youtube },
                     { key: 'telegram', label: 'Telegram', icon: <TelegramIcon fontSize="small" />, url: activeContact.telegram },
                   ].filter(item => {
                     if (!item.url || !item.url.trim()) return false;
                     if (activeContact.social_visibility && activeContact.social_visibility[item.key] === false) {
                       return false;
                     }
                     return true;
                   });

                   if (socialList.length === 0) return null;

                   return (
                     <Box sx={{ mt: 1, mb: 3, pt: 2, borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                       <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                         {t('socialMedia') || 'Social Profiles'}
                       </Typography>
                       <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                         {socialList.map(item => (
                           <IconButton
                             key={item.key}
                             size="small"
                             component="a"
                             href={item.url}
                             target="_blank"
                             rel="noopener noreferrer"
                             aria-label={item.label}
                             sx={{
                               backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                               color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                               '&:hover': {
                                 color: theme => theme.palette.primary.main,
                                 backgroundColor: isDarkMode ? 'rgba(46, 91, 255, 0.15)' : 'rgba(46, 91, 255, 0.1)',
                               }
                             }}
                           >
                             {item.icon}
                           </IconButton>
                         ))}
                       </Box>
                     </Box>
                   );
                 })()}

                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: isDarkMode ? 'rgba(46, 91, 255, 0.05)' : 'rgba(46, 91, 255, 0.03)',
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(46, 91, 255, 0.1)' : 'rgba(46, 91, 255, 0.08)',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('availability') !== 'availability'
                      ? t('availability')
                      : 'Our team is available Monday through Friday from 9:00 AM to 6:00 PM (GMT+4). We typically respond to inquiries within 24 hours.'}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? 'background.paper' : 'background.paper',
                }}
              >
                <Typography
                  variant="h4"
                  component="h3"
                  gutterBottom
                  sx={{ mb: 4, fontWeight: 600 }}
                >
                  {t('Send Message') || 'Send a Message'}
                </Typography>

                <form onSubmit={handleSubmit} noValidate>
                  {/* 🍯 Honeypot - invisible to humans, filled by bots */}
                  <input
                    type="text"
                    name="_honeypot"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('nameLabel')}
                        name="name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        error={!!errors.name}
                        helperText={errors.name}
                        aria-label={t('nameLabel')}
                        inputProps={{ 'aria-required': 'true' }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('emailLabel')}
                        name="email"
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={!!errors.email}
                        helperText={errors.email}
                        aria-label={t('emailLabel')}
                        inputProps={{ 'aria-required': 'true' }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('messageLabel')}
                        name="message"
                        variant="outlined"
                        multiline
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        error={!!errors.message}
                        helperText={errors.message}
                        aria-label={t('messageLabel')}
                        inputProps={{ 'aria-required': 'true' }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {/* Rate limit warning */}
                      {rateLimitMsg && (
                        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                          {cooldownLeft > 0
                            ? `Please wait ${cooldownLeft} second${cooldownLeft !== 1 ? 's' : ''} before submitting again.`
                            : rateLimitMsg}
                        </Alert>
                      )}
                      {submitStatus === 'success' && (
                        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                          {t('Message sent successfully!') || 'Message sent successfully!'}
                        </Alert>
                      )}
                      {submitStatus === 'error' && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                          {t('შეცდომა!') || 'Error sending message. Please try again.'}
                        </Alert>
                      )}
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: 2, padding: '12px' }}
                        disabled={isSubmitting || cooldownLeft > 0}
                      >
                        {isSubmitting
                          ? (t('Sending...') || 'Sending...')
                          : cooldownLeft > 0
                          ? `Wait ${cooldownLeft}s...`
                          : (t('Sending Message') || 'Send Message')}
                      </Button>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 1.5, opacity: 0.75 }}>
                        <SecurityIcon sx={{ fontSize: 13, color: '#00C896' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                          {isBotDefenseActive 
                            ? `Protected by ${provider === 'turnstile' ? 'Cloudflare Turnstile' : 'reCAPTCHA v3'} (Invisible)` 
                            : 'Protected by Zero-Friction Anti-Spam'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
